import { supabase } from "@/integrations/supabase/client";
import { getUtmData } from "@/lib/utm";
import { mailchimpSubscribe } from "@/lib/mailchimpSubscribe.functions";

/**
 * Tag vocabulary that already exists in the GoHub Marketing audience.
 * Keep these strings exactly — Mailchimp tag names are case-sensitive.
 */
export type MailchimpTag =
  | "stage:subscriber"
  | "stage:demo-requested"
  | "stage:customer"
  | "stage:churned"
  | "source:newsletter"
  | "source:demo"
  | "source:signup"
  | "source:linkedin"
  | "source:event"
  | "source:import"
  | "source:contact"
  | "source:blog"
  | "persona:optician"
  | "persona:pharmacy"
  | "persona:brand"
  | "persona:retailer"
  | "persona:unknown"
  | "consent:marketing"
  | "consent:marketing-pending"
  | "page:home"
  | "page:opticians"
  | "page:pharmacy"
  | "page:brands"
  | "page:retailer"
  | "page:blog"
  | "page:launch"
  | "page:demo"
  | "page:updates";

export interface MailchimpSubscribeInput {
  email: string;
  firstName?: string | undefined;
  lastName?: string | undefined;
  phone?: string | undefined;
  company?: string | undefined;
  /** Tags to set to `active` on this contact. Only these tags are touched. */
  tagsToAdd: (MailchimpTag | string)[];
  /** Tags to set to `inactive` on this contact. Only these tags are touched. */
  tagsToRemove?: (MailchimpTag | string)[] | undefined;
  /** Initial subscription status for new contacts. Existing status is never changed. */
  status?: "subscribed" | "pending" | undefined;
  /**
   * Exact page path the user submitted from (e.g. "/pharmacy" or
   * "/blog/why-most-vto-tools-disappoint"). Stored in the SIGNUPPG merge field.
   * If omitted, auto-derived from window.location.pathname.
   */
  signupPage?: string | undefined;
}

/** Blog slug → persona mapping for blog-post-driven signups. */
const BLOG_SLUG_PERSONA: Record<string, MailchimpTag> = {
  "why-most-vto-tools-disappoint": "persona:optician",
  "how-online-pharmacy-sell-prescription-glasses": "persona:pharmacy",
  "building-prescription-eyewear-catalog-without-inventory": "persona:brand",
  "how-to-start-online-eyewear-business-2026": "persona:retailer",
};

/** Coarse page-area tag from a pathname. */
export function pageAreaTag(path: string | undefined): MailchimpTag | null {
  if (!path) return null;
  const p = path.toLowerCase().replace(/\/+$/, "") || "/";
  if (p === "/" || p === "") return "page:home";
  if (p.startsWith("/opticians")) return "page:opticians";
  if (p.startsWith("/pharmacy")) return "page:pharmacy";
  if (p.startsWith("/brands-resellers") || p.startsWith("/brands")) return "page:brands";
  if (p.startsWith("/retailer")) return "page:retailer";
  if (p.startsWith("/launch")) return "page:launch";
  if (p.startsWith("/demo")) return "page:demo";
  if (p.startsWith("/updates")) return "page:updates";
  if (p.startsWith("/blog")) return "page:blog";
  return null;
}

/** Extract blog slug from "/blog/<slug>" path, or null. */
export function blogSlugFromPath(path: string | undefined): string | null {
  if (!path) return null;
  const m = path.toLowerCase().match(/^\/blog\/([^/?#]+)/);
  return m?.[1] ?? null;
}

/** Persona derived from the blog post slug (only for the curated set). */
export function personaFromBlogSlug(slug: string | undefined): MailchimpTag | null {
  if (!slug) return null;
  return BLOG_SLUG_PERSONA[slug] ?? null;
}

/**
 * Subscribe a contact to the GoHub Marketing Mailchimp audience.
 * Auto-injects:
 *   - signupPage = window.location.pathname (unless explicitly provided)
 *   - page:* area tag based on the path
 *   - source:blog when the path is a blog post (in addition to page:blog)
 *   - persona:* derived from the blog post slug (only when one is curated AND
 *     the caller did not already provide a persona)
 *   - source:linkedin / source:event when utm_source matches
 * Never throws and never blocks the form — failures are logged only.
 */
export async function subscribeToMailchimp(
  input: MailchimpSubscribeInput,
): Promise<void> {
  try {
    const path =
      input.signupPage ??
      (typeof window !== "undefined" ? window.location.pathname : undefined);

    const tagsToAdd = new Set<string>(input.tagsToAdd);

    // page:* area tag (coarse segmentation)
    const areaTag = pageAreaTag(path);
    if (areaTag) tagsToAdd.add(areaTag);

    // Blog: do NOT auto-add source:blog (one source tag per contact — the
    // caller already provides source:newsletter / source:contact / etc.).
    // Persona default: caller-supplied wins; otherwise derive from curated
    // blog slug, then from page area, then fall back to persona:unknown.
    const slug = blogSlugFromPath(path);
    const callerSetPersona = [...tagsToAdd].some((t) => t.startsWith("persona:"));
    if (!callerSetPersona) {
      const blogPersona = slug ? personaFromBlogSlug(slug) : null;
      const pagePersona = personaFromPath(path);
      tagsToAdd.add(blogPersona ?? (pagePersona !== "persona:unknown" ? pagePersona : "persona:unknown"));
    }

    // UTM-driven source tags (linkedin / event only, per spec)
    try {
      const utm = getUtmData();
      const src = (utm.utm_source ?? "").toLowerCase();
      if (src === "linkedin") tagsToAdd.add("source:linkedin");
      else if (src === "event") tagsToAdd.add("source:event");
    } catch {
      /* ignore */
    }

    try {
      await mailchimpSubscribe({
        data: {
          email: input.email,
          firstName: input.firstName,
          lastName: input.lastName,
          phone: input.phone,
          company: input.company,
          signupPage: path,
          tagsToAdd: [...tagsToAdd],
          tagsToRemove: input.tagsToRemove ?? [],
          status: input.status ?? "subscribed",
        },
      });
    } catch (error) {
      console.error("Mailchimp subscribe error:", error);
    }
  } catch (e) {
    console.error("Mailchimp subscribe threw:", e);
  }
}

/** Map a full name string into first / last halves (split on first space). */
export function splitName(full: string | undefined): {
  firstName?: string | undefined;
  lastName?: string | undefined;
} {
  if (!full) return {};
  const parts = full.trim().split(/\s+/);
  if (parts.length === 0) return {};
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" ") || undefined,
  };
}

/** Derive the persona tag from the role dropdown on the demo / launch forms. */
export function personaFromRole(role: string | undefined): MailchimpTag {
  if (!role) return "persona:unknown";
  const r = role.toLowerCase();
  if (r.includes("optician")) return "persona:optician";
  if (r.includes("pharmac")) return "persona:pharmacy";
  if (r.includes("brand") || r.includes("reseller")) return "persona:brand";
  if (r.includes("retail")) return "persona:retailer";
  return "persona:unknown";
}

/** Derive the persona tag from a referrer or landing path, e.g. "/pharmacy". */
export function personaFromPath(path: string | undefined): MailchimpTag {
  if (!path) return "persona:unknown";
  const p = path.toLowerCase();
  if (p.includes("/opticians")) return "persona:optician";
  if (p.includes("/pharmacy")) return "persona:pharmacy";
  if (p.includes("/brands-resellers") || p.includes("/brands")) return "persona:brand";
  if (p.includes("/retailer")) return "persona:retailer";
  return "persona:unknown";
}

/** Best persona signal we can derive in the browser. */
export function detectPersona(roleField?: string): MailchimpTag {
  const fromRole = roleField ? personaFromRole(roleField) : null;
  if (fromRole && fromRole !== "persona:unknown") return fromRole;
  if (typeof window === "undefined") return "persona:unknown";
  // Blog post slug → persona, if curated.
  const blog = personaFromBlogSlug(blogSlugFromPath(window.location.pathname) ?? undefined);
  if (blog) return blog;
  // Try the referring path (e.g. /pharmacy → persona:pharmacy)
  try {
    const ref = document.referrer ? new URL(document.referrer).pathname : "";
    const fromRef = personaFromPath(ref);
    if (fromRef !== "persona:unknown") return fromRef;
  } catch {
    /* ignore */
  }
  return personaFromPath(window.location.pathname);
}
